type ApiKey = {
  categoryId: string;
  callId?: string;
  prefix?: string;
};

type ControllerKey = ApiKey & { callId: string; mapKey: string };

export const makeApiKey = ({ categoryId, callId = 'default', prefix = 'dashboard' }: ApiKey) => {
  return `${prefix}--${categoryId}--${callId}`;
};

const parseKey = (key: string): ControllerKey => {
  if (!key) {
    return {
      categoryId: '',
      callId: '',
      prefix: undefined,
      mapKey: '',
    };
  }

  const [prefix, categoryId, callId] = key.split('--');
  return {
    prefix,
    categoryId,
    callId: callId || 'default',
    mapKey: `${prefix}--${categoryId}`,
  };
};

const abortApiControllerMap: Map<
  ApiKey['categoryId'],
  Map<ApiKey['callId'], AbortController>
> = new Map();

export const abortApiController = {
  setMap: (key: string) => {
    const { categoryId, callId, mapKey } = parseKey(key);
    if (!categoryId) {
      return;
    }

    const categoryMap = abortApiControllerMap.get(mapKey);

    if (categoryMap && !categoryMap.has(callId)) {
      categoryMap.set(callId, new AbortController());
    } else if (!categoryMap) {
      abortApiControllerMap.set(mapKey, new Map([[callId, new AbortController()]]));
    }
  },
  getSignal: (key: string) => {
    const { mapKey, callId } = parseKey(key);

    const categoryMap = abortApiControllerMap.get(mapKey);
    if (categoryMap) {
      return categoryMap.get(callId)?.signal ?? null;
    }
    return null;
  },
  abortAll: () => {
    Array.from(abortApiControllerMap.values()).forEach((categoryMap) => {
      Array.from(categoryMap.values()).forEach((controller) => {
        controller.abort();
      });
    });
    abortApiControllerMap.clear();
  },
  abortCategory: (key?: string) => {
    if (!key) {
      return;
    }

    const { mapKey, categoryId } = parseKey(key);
    if (!categoryId) {
      return;
    }

    const categoryMap = abortApiControllerMap.get(mapKey);
    if (categoryMap) {
      Array.from(categoryMap.values()).forEach((controller) => {
        controller.abort();
      });
      abortApiControllerMap.delete(mapKey);
    }
  },
  abortOtherCategories: (key?: string) => {
    if (!key) {
      return;
    }

    const { mapKey, categoryId } = parseKey(key);
    if (!categoryId) {
      return;
    }

    Array.from(abortApiControllerMap.keys()).forEach((controllerKey) => {
      if (controllerKey !== mapKey) {
        const categoryMap = abortApiControllerMap.get(controllerKey);
        if (categoryMap) {
          Array.from(categoryMap.values()).forEach((controller) => {
            controller.abort();
          });
          abortApiControllerMap.delete(controllerKey);
        }
      }
    });
  },
  abortCallsByCategory: (keys?: string[]) => {
    if (!Array.isArray(keys)) {
      return;
    }

    const keyMap = new Map<string, Set<string>>();

    for (let i = 0; i < keys.length; i++) {
      const { mapKey, callId } = parseKey(keys[i]);
      const categoryMap = keyMap.get(mapKey);
      if (categoryMap) {
        categoryMap.add(callId);
      } else {
        keyMap.set(mapKey, new Set([callId]));
      }
    }

    Array.from(keyMap.keys()).forEach((mapKey) => {
      const categoryMap = abortApiControllerMap.get(mapKey);
      const callIdSet = keyMap.get(mapKey);
      if (categoryMap && callIdSet) {
        Array.from(callIdSet).forEach((callId) => {
          const controller = categoryMap.get(callId);
          if (controller) {
            controller.abort();
            categoryMap.delete(callId);
          }
        });
        if (categoryMap.size === 0) {
          abortApiControllerMap.delete(mapKey);
        }
      }
    });
  },
};
